import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "utils";

// Each variant is a complete, self-contained semantic style (size + weight +
// color). There is no separate color axis: pick the role-named variant that
// matches the intent. Neutral text uses the zinc `secondary-*` ramp; primary
// text is `secondary-800` (matches `body { text-secondary-800 }` in index.css).
const textVariants = cva("", {
  variants: {
    variant: {
      // Headings (primary text)
      display: "text-7xl font-semibold text-secondary-800",
      displaySmall: "text-4xl font-medium text-secondary-800",
      pageHeading: "text-2xl font-semibold text-secondary-800",
      heading: "text-xl font-semibold text-secondary-800",
      headingMuted: "text-xl font-semibold text-secondary-500",
      title: "text-sm font-semibold text-secondary-800",
      // Primary body text
      body: "text-sm text-secondary-800",
      bodyStrong: "text-sm font-semibold text-secondary-800",
      label: "text-sm font-medium text-secondary-800",
      labelSmall: "text-xs font-medium text-secondary-800",
      // Secondary / de-emphasized text
      detail: "text-sm font-medium text-secondary-600",
      muted: "text-sm text-secondary-500",
      subtle: "text-sm text-secondary-400",
      caption: "text-xs text-secondary-500",
      captionStrong: "text-xs font-semibold text-secondary-500",
      overline:
        "text-xs font-semibold text-secondary-500 uppercase tracking-wide",
      // Darker overline for use on tinted/filled surfaces where secondary-500
      // drops below the 4.5:1 contrast floor.
      overlineStrong:
        "text-xs font-semibold text-secondary-700 uppercase tracking-wide",
      // Date tile (see UpcomingSeasonalEvents). secondary-600 rather than the
      // overline default, which fails contrast on the tinted tile.
      dateTileMonth:
        "text-xs font-semibold text-secondary-600 uppercase leading-none tracking-wide",
      dateTileDay: "text-xl font-semibold text-secondary-800 leading-none",
      dateTileMonthAlone:
        "text-xl font-semibold text-secondary-800 uppercase leading-none tracking-tight",
      // State / brand
      error: "text-sm text-system-error",
      errorSmall: "text-xs text-system-error",
      link: "text-sm font-medium text-primary-600",
      accent: "text-sm text-primary-500",
      // Metric (zinc neutral, matched to the secondary ramp)
      metric: "text-sm text-secondary-500",
      metricCaption: "text-xs text-secondary-500",
      // Italic counterparts (de-emphasized, quoted, or placeholder text)
      mutedItalic: "text-sm text-secondary-500 italic",
      subtleItalic: "text-sm text-secondary-400 italic",
      captionItalic: "text-xs text-secondary-500 italic",
      errorItalic: "text-sm text-system-error italic",
      metricItalic: "text-sm text-secondary-500 italic",
      // Dark-surface only (white-on-dark with opacity)
      tooltip: "text-white/70",
      tooltipLabel: "text-[10px] text-white/50",
      // White-on-tinted count badge (see CountBadge).
      countBadge: "text-2xs font-bold leading-none text-white",
    },
    // `numeric` is an orthogonal axis for digit rendering, combinable with any
    // `variant`. `tabular` keeps the regular font but makes every digit the same
    // width so figures line up in columns; `mono` switches to the monospace
    // family (also fixed-width) for a distinct, code-like look.
    numeric: {
      default: "",
      tabular: "tabular-nums",
      mono: "font-mono tabular-nums",
    },
  },
  defaultVariants: { variant: "body", numeric: "default" },
});

type TextElement =
  | "p"
  | "span"
  | "div"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "label"
  | "dt"
  | "dd"
  | "strong";

type TextProps<T extends TextElement = "p"> = VariantProps<
  typeof textVariants
> & {
  as?: T;
  className?: string;
  children?: React.ReactNode;
} & Omit<
    React.ComponentPropsWithRef<T>,
    "as" | "className" | "children" | "variant" | "numeric"
  >;

function Text<T extends TextElement = "p">({
  as,
  variant,
  numeric,
  className,
  children,
  ...props
}: TextProps<T>) {
  const Comp = as ?? "p";
  return (
    <Comp
      className={cn(textVariants({ variant, numeric }), className)}
      {...(props as Record<string, unknown>)}
    >
      {children}
    </Comp>
  );
}

export { Text, textVariants, type TextProps, type TextElement };
